/**
 * Layer-Orientation CTE Utilities
 * Multi-angle (dynamic) CTE scaling driven by the robot toolpath.
 *
 *  1. Parse a KUKA KRL .src (optionally with its .dat) or G-code toolpath.
 *  2. Split it into layers (slicer layer markers, or tool-axis / height changes).
 *  3. Fit each layer's normal (plane fit, or tool axis from A/B/C).
 *  4. Group consecutive layers with the same normal into orientation regions.
 *  5. Give each region its own rotated anisotropic CTE strain tensor
 *     (bead direction = material X, in-layer transverse = Y, layer normal = Z).
 *  6. Blend the regions into one continuous displacement field that can be
 *     evaluated anywhere on the STEP / STL geometry.
 *
 * The field evaluation (evalLayerField / serializeLayerField) is mirrored
 * server-side in services/cte/layer-field.js — keep the two in step.
 */

// ─── SMALL VECTOR HELPERS ─────────────────────────────────────────────────────
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a) => Math.sqrt(dot(a, a));
const normalize = (a) => { const l = norm(a); return l > 0 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 0]; };
const scale = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const angleDeg = (a, b) => Math.acos(Math.max(-1, Math.min(1, dot(a, b)))) * 180 / Math.PI;

// ─── KUKA ORIENTATION ─────────────────────────────────────────────────────────
// KUKA A/B/C are Z-Y'-X'' Euler angles: R = Rz(A) * Ry(B) * Rx(C).
export function kukaABCToMatrix(A, B, C) {
  const a = A * Math.PI / 180, b = B * Math.PI / 180, c = C * Math.PI / 180;
  const ca = Math.cos(a), sa = Math.sin(a);
  const cb = Math.cos(b), sb = Math.sin(b);
  const cc = Math.cos(c), sc = Math.sin(c);
  return [
    [ca * cb, ca * sb * sc - sa * cc, ca * sb * cc + sa * sc],
    [sa * cb, sa * sb * sc + ca * cc, sa * sb * cc - ca * sc],
    [-sb, cb * sc, cb * cc],
  ];
}

// Tool Z axis (third column) for a KUKA A/B/C orientation.
export function kukaToolAxis(A, B, C) {
  const R = kukaABCToMatrix(A, B, C);
  return [R[0][2], R[1][2], R[2][2]];
}

// ─── SYMMETRIC 3x3 EIGEN (JACOBI) ─────────────────────────────────────────────
// Returns eigenvalues ascending with matching unit eigenvectors.
export function eigSym3(M) {
  const a = [[M[0][0], M[0][1], M[0][2]], [M[1][0], M[1][1], M[1][2]], [M[2][0], M[2][1], M[2][2]]];
  const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 50; sweep++) {
    const off = Math.abs(a[0][1]) + Math.abs(a[0][2]) + Math.abs(a[1][2]);
    if (off < 1e-14 * (Math.abs(a[0][0]) + Math.abs(a[1][1]) + Math.abs(a[2][2]) + 1e-300)) break;
    for (const [p, q] of [[0, 1], [0, 2], [1, 2]]) {
      if (Math.abs(a[p][q]) < 1e-300) continue;
      const theta = (a[q][q] - a[p][p]) / (2 * a[p][q]);
      const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      const c = 1 / Math.sqrt(t * t + 1), s = t * c;
      for (let k = 0; k < 3; k++) {
        const akp = a[k][p], akq = a[k][q];
        a[k][p] = c * akp - s * akq;
        a[k][q] = s * akp + c * akq;
      }
      for (let k = 0; k < 3; k++) {
        const apk = a[p][k], aqk = a[q][k];
        a[p][k] = c * apk - s * aqk;
        a[q][k] = s * apk + c * aqk;
      }
      for (let k = 0; k < 3; k++) {
        const vkp = v[k][p], vkq = v[k][q];
        v[k][p] = c * vkp - s * vkq;
        v[k][q] = s * vkp + c * vkq;
      }
    }
  }
  const order = [0, 1, 2].sort((i, j) => a[i][i] - a[j][j]);
  return {
    values: order.map(i => a[i][i]),
    vectors: order.map(i => normalize([v[0][i], v[1][i], v[2][i]])),
  };
}

// ─── TOOLPATH PARSING ─────────────────────────────────────────────────────────
// Toolpath record: flat arrays, one entry per motion point.
//   pos       Float64Array [x,y,z,...]
//   toolAxis  Float64Array [nx,ny,nz,...]  (0,0,0 when no orientation known)
//   travel    Uint8Array   1 = non-printing move
//   layerTag  Int32Array   slicer layer marker active at this point (-1 = none)
//   order     number of points
function makeToolpathBuilder() {
  const pos = [], axis = [], travel = [], layerTag = [];
  return {
    push(p, n, isTravel, tag) {
      pos.push(p[0], p[1], p[2]);
      axis.push(n ? n[0] : 0, n ? n[1] : 0, n ? n[2] : 0);
      travel.push(isTravel ? 1 : 0);
      layerTag.push(tag);
    },
    finish(stats) {
      return {
        pos: Float64Array.from(pos),
        toolAxis: Float64Array.from(axis),
        travel: Uint8Array.from(travel),
        layerTag: Int32Array.from(layerTag),
        count: travel.length,
        stats,
      };
    },
  };
}

const NUM = '[-+]?(?:\\d+\\.?\\d*|\\.\\d+)(?:[eE][-+]?\\d+)?';
const COMPONENT_RE = new RegExp(`\\b(X|Y|Z|A|B|C|E[1-6])\\s+(${NUM})`, 'gi');

function parseAggregate(body) {
  const out = {};
  const clean = body.replace(/^\s*[A-Z_0-9]+\s*:/i, ''); // strip "E6POS:" / "POS:" / "FRAME:"
  let m;
  COMPONENT_RE.lastIndex = 0;
  while ((m = COMPONENT_RE.exec(clean)) !== null) {
    out[m[1].toUpperCase()] = parseFloat(m[2]);
  }
  return out;
}

// Layer markers emitted by slicers in comments or as variable assignments:
//   ;LAYER:12  ;LAYER_CHANGE: 12  ; Layer 12  iLayer = 12  set global.print_layer = 12
const LAYER_NUM_COMMENT_RE = /\bLAYER(?:_CHANGE|_?NUM(?:BER)?|_?NO|_?INDEX)?\b\s*[:=#]?\s*(-?\d+)/i;
const LAYER_BARE_COMMENT_RE = /^\s*LAYER_CHANGE\s*$/i;
// e.g. `iLayer = 12`, `LAYER_NO=3`, `set global.print_layer = 12` — not `nLayers = 21`
const LAYER_VAR_RE = /^\s*(?:set\s+)?[\w.]*LAYER(?:_?(?:NO|NUM|NUMBER|IDX|INDEX))?\s*=\s*(-?\d+)\s*$/i;
const TYPE_COMMENT_RE = /\b(?:LINE_)?TYPE\s*:\s*([A-Z_\- ]+)/i;

function makeLayerTracker() {
  let tag = -1;
  let auto = 0;
  return {
    get tag() { return tag; },
    get found() { return auto > 0 || tag !== -1; },
    // returns true if a marker was found on this line
    scan(code, comment) {
      if (comment) {
        const m = comment.match(LAYER_NUM_COMMENT_RE);
        if (m) { tag = parseInt(m[1], 10); auto++; return true; }
        if (LAYER_BARE_COMMENT_RE.test(comment)) { tag = tag < 0 ? 0 : tag + 1; auto++; return true; }
      }
      if (code) {
        const m = code.match(LAYER_VAR_RE);
        if (m && !/^\s*(DECL|INT|REAL)\b/i.test(code)) { tag = parseInt(m[1], 10); auto++; return true; }
      }
      return false;
    },
  };
}

// Point on the circle through s, a, e reached by turning `angleDeg` from s,
// in the sense s → a. Returns null for collinear points.
function circlePoint(s, a, e, angleDeg) {
  const u = [a[0] - s[0], a[1] - s[1], a[2] - s[2]];
  const v = [e[0] - s[0], e[1] - s[1], e[2] - s[2]];
  const n = cross(u, v);
  const nn = dot(n, n);
  if (nn < 1e-12) return null;
  // Circumcentre of the triangle s, a, e
  const uu = dot(u, u), vv = dot(v, v);
  const t1 = scale(cross(n, u), vv), t2 = scale(cross(v, n), uu);
  const c = [s[0] + (t1[0] + t2[0]) / (2 * nn), s[1] + (t1[1] + t2[1]) / (2 * nn), s[2] + (t1[2] + t2[2]) / (2 * nn)];
  const k = normalize(n);
  const r = [s[0] - c[0], s[1] - c[1], s[2] - c[2]];
  const th = angleDeg * Math.PI / 180;
  const kr = cross(k, r);
  return [
    c[0] + r[0] * Math.cos(th) + kr[0] * Math.sin(th),
    c[1] + r[1] * Math.cos(th) + kr[1] * Math.sin(th),
    c[2] + r[2] * Math.cos(th) + kr[2] * Math.sin(th),
  ];
}

// Signed angle (deg, 0..360) from s to p around the circle s → a → e
function arcAngle(s, a, e, p) {
  const u = [a[0] - s[0], a[1] - s[1], a[2] - s[2]];
  const v = [e[0] - s[0], e[1] - s[1], e[2] - s[2]];
  const n = cross(u, v);
  const nn = dot(n, n);
  if (nn < 1e-12) return 0;
  const uu = dot(u, u), vv = dot(v, v);
  const t1 = scale(cross(n, u), vv), t2 = scale(cross(v, n), uu);
  const c = [s[0] + (t1[0] + t2[0]) / (2 * nn), s[1] + (t1[1] + t2[1]) / (2 * nn), s[2] + (t1[2] + t2[2]) / (2 * nn)];
  const k = normalize(n);
  const r0 = [s[0] - c[0], s[1] - c[1], s[2] - c[2]];
  const r1 = [p[0] - c[0], p[1] - c[1], p[2] - c[2]];
  const ang = Math.atan2(dot(k, cross(r0, r1)), dot(r0, r1)) * 180 / Math.PI;
  return ang < 0 ? ang + 360 : ang;
}

const MOTION_RE = /^\s*(S?(?:PTP|LIN|CIRC)(?:_REL)?|SPL)\b(.*)$/i;
const EXTRUDER_SPEED_RE = /TRIGGER[^\n;]*\bDO\s+[\w.$]*SPEED\w*\s*=/i;
const EXTRUDER_SPEED_SET_RE = /\bDO\s+[\w.$]*SPEED\w*\s*=\s*([-+]?(?:\d+\.?\d*|\.\d+))/i;
const ASSIGN_RE = /^\s*(?:DECL\s+(?:GLOBAL\s+)?\w+\s+)?([A-Z_]\w*)\s*=\s*\{([^}]*)\}/i;
const ARG_TOKEN_RE = /\{[^}]*\}|[A-Z_$][\w.$]*|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/gi;

/**
 * Parse KUKA KRL. `text` may be the .src alone or the .dat followed by the .src;
 * named positions (DECL E6POS XP1={...}) are resolved from either. Assignments
 * are applied in program order, so a point reassigned in the .src takes the
 * value current at each move.
 */
export function parseKukaSRC(text) {
  const lines = text.split(/\r?\n/);
  const named = new Map();
  const jointNamed = new Set(); // DECL AXIS / E6AXIS points: joint space, no XYZ
  const hasXYZ = (body) => /\b[XYZ]\s+[-+.\d]/i.test(body);
  const isJointAgg = (body) => /\bA[1-6]\s+[-+.\d]/i.test(body) && !hasXYZ(body);
  // Declarations (.dat or DECL in the .src) are visible to the whole program
  for (const raw of lines) {
    const code = raw.split(';')[0];
    if (!/^\s*DECL\b/i.test(code)) continue;
    const m = code.match(ASSIGN_RE);
    if (m && hasXYZ(m[2])) named.set(m[1].toUpperCase(), parseAggregate(m[2]));
    else if (m && isJointAgg(m[2])) jointNamed.add(m[1].toUpperCase());
  }

  const b = makeToolpathBuilder();
  const layers = makeLayerTracker();
  const cur = { X: 0, Y: 0, Z: 0, A: null, B: null, C: null };
  const ext = {};
  let haveOrientation = false;
  let typeTravel = false;
  let moves = 0, unresolved = 0, extVarying = false, circAngles = 0, jointMoves = 0;
  // Extruder on/off from triggers such as "TRIGGER WHEN DISTANCE=0 DELAY=0
  // DO SPEED=1.71" (PE320 / AiSync output). When a program uses them, a move
  // is only printed while the last commanded speed is > 0; approach and
  // retract moves before/after extrusion are travel.
  const usesExtruderSpeed = EXTRUDER_SPEED_RE.test(text);
  let extruding = null;

  const toolAxisNow = () => (cur.A !== null && cur.B !== null && cur.C !== null
    ? kukaToolAxis(cur.A, cur.B, cur.C) : null);

  // Resolve an aggregate (absolute, or relative to `base`) to a full pose
  const resolve = (agg, base, isRel) => {
    const pose = { ...base };
    for (const k of ['X', 'Y', 'Z']) if (agg[k] !== undefined) pose[k] = isRel ? base[k] + agg[k] : agg[k];
    for (const k of ['A', 'B', 'C']) {
      if (agg[k] !== undefined) {
        pose[k] = isRel && base[k] !== null ? base[k] + agg[k] : agg[k];
        haveOrientation = true;
      }
    }
    for (const k of Object.keys(agg)) {
      if (k[0] === 'E') {
        if (ext[k] !== undefined && Math.abs(ext[k] - agg[k]) > 1e-6) extVarying = true;
        ext[k] = agg[k];
      }
    }
    return pose;
  };
  const moveTo = (pose, isTravel) => {
    Object.assign(cur, pose);
    b.push([cur.X, cur.Y, cur.Z], toolAxisNow(), isTravel, layers.tag);
    moves++;
  };

  for (const raw of lines) {
    const semi = raw.indexOf(';');
    const code = semi >= 0 ? raw.slice(0, semi) : raw;
    const comment = semi >= 0 ? raw.slice(semi + 1) : '';

    if (comment) {
      const t = comment.match(TYPE_COMMENT_RE);
      if (t) typeTravel = /TRAVEL|MOVE|RETRACT|WIPE/i.test(t[1]);
    }
    layers.scan(code, comment);

    if (usesExtruderSpeed) {
      const sp = code.match(EXTRUDER_SPEED_SET_RE);
      if (sp) extruding = parseFloat(sp[1]) > 0;
      // End-of-print markers: ";PROCESS_OFF", HALT_MOTOR=TRUE, STOP_SERVO=TRUE
      if (/\bPROCESS_OFF\b/i.test(comment) || /\b(HALT_MOTOR|STOP_SERVO)\s*=\s*TRUE\b/i.test(code)) extruding = false;
    }

    const asg = code.match(ASSIGN_RE);
    if (asg && hasXYZ(asg[2]) && !MOTION_RE.test(code)) {
      named.set(asg[1].toUpperCase(), parseAggregate(asg[2]));
      continue;
    }

    const mm = code.match(MOTION_RE);
    if (!mm) continue;
    const kind = mm[1].toUpperCase();
    const isRel = kind.endsWith('_REL');
    const isTravel = kind.includes('PTP') || typeTravel || (usesExtruderSpeed && extruding !== true);
    const isCirc = kind.includes('CIRC');

    // Arguments in order: {aggregate} or named point; stop at approximation
    // / trigger keywords. CIRC may carry ", CA <deg>".
    const aggs = [];
    let ca = null, missing = false, jointOnly = false;
    const tokens = mm[2].match(ARG_TOKEN_RE) || [];
    for (let i = 0; i < tokens.length; i++) {
      const tok = tokens[i];
      const up = tok.toUpperCase();
      if (tok[0] === '{') { aggs.push(parseAggregate(tok.slice(1, -1))); continue; }
      if (up === 'CA' && i + 1 < tokens.length) { ca = parseFloat(tokens[++i]); continue; }
      if (/^(C_|WITH$|TRIGGER$)/.test(up)) break;
      if (/^[-+.\d]/.test(tok)) continue;
      // System positions ($POS_ACT, $AXIS_HOME, ...) and joint-space points
      // have no Cartesian pose to record
      if (up[0] === '$' || jointNamed.has(up)) { jointOnly = true; continue; }
      const hit = named.get(up);
      if (hit) aggs.push(hit); else missing = true;
    }
    if (jointOnly && !aggs.length && !missing) { jointMoves++; continue; }
    if (!aggs.length || (isCirc && aggs.length < 2) || missing) { unresolved++; continue; }

    if (!isCirc) {
      const agg = aggs[0];
      // Axis-specific PTP {A1 ..,A6 ..} has no Cartesian pose: skip it
      if (!isRel && agg.X === undefined && agg.Y === undefined && agg.Z === undefined
        && agg.A === undefined && agg.B === undefined && agg.C === undefined) { jointMoves++; continue; }
      moveTo(resolve(agg, cur, isRel), isTravel);
      continue;
    }

    // CIRC aux, end: both relative to the arc start for CIRC_REL
    const start = { ...cur };
    const aux = resolve(aggs[0], start, isRel);
    const end = resolve(aggs[1], start, isRel);
    const sP = [start.X, start.Y, start.Z], aP = [aux.X, aux.Y, aux.Z], eP = [end.X, end.Y, end.Z];
    let sweep = arcAngle(sP, aP, eP, eP);
    if (ca !== null && Number.isFinite(ca)) { sweep = ca; circAngles++; }
    // Sample the arc so the deposited bead is represented, not just its chord
    const steps = Math.max(1, Math.min(72, Math.ceil(Math.abs(sweep) / 10)));
    for (let k = 1; k <= steps; k++) {
      const p = circlePoint(sP, aP, eP, sweep * k / steps);
      const pose = { ...end };
      if (p) { pose.X = p[0]; pose.Y = p[1]; pose.Z = p[2]; }
      moveTo(pose, isTravel);
    }
  }

  return b.finish({
    format: 'KUKA KRL',
    lines: lines.length,
    moves,
    unresolved,
    namedPositions: named.size,
    hasOrientation: haveOrientation,
    hasLayerMarkers: layers.found,
    externalAxesVarying: extVarying,
    circAngles,
    jointMoves,
    extruderTriggers: usesExtruderSpeed,
  });
}

/**
 * Parse G-code (planar or 5-axis with A/B/C in degrees about X/Y/Z).
 * G0 and G1 without positive extrusion are treated as travel.
 */
export function parseGcodeToolpath(text) {
  const lines = text.split(/\r?\n/);
  const b = makeToolpathBuilder();
  const layers = makeLayerTracker();
  const cur = { X: 0, Y: 0, Z: 0, A: null, B: null, C: null };
  let absolute = true, absE = false, lastE = 0;
  let moves = 0, haveOrientation = false, typeTravel = false;
  const wordRe = new RegExp(`([GXYZABCEF])(${NUM})`, 'gi');

  for (const raw of lines) {
    const semi = raw.indexOf(';');
    const code = (semi >= 0 ? raw.slice(0, semi) : raw).trim();
    const comment = semi >= 0 ? raw.slice(semi + 1) : '';
    if (comment) {
      const t = comment.match(TYPE_COMMENT_RE);
      if (t) typeTravel = /TRAVEL|MOVE|RETRACT|WIPE/i.test(t[1]);
    }
    layers.scan(code, comment);
    if (!code) continue;
    if (/^G90\b/i.test(code)) absolute = true;
    if (/^G91\b/i.test(code)) absolute = false;
    if (/^M82\b/i.test(code)) absE = true;
    if (/^M83\b/i.test(code)) absE = false;
    const g = code.match(/^G0*([0-3])\b/i);
    if (!g) continue;

    const w = {};
    let m;
    wordRe.lastIndex = 0;
    while ((m = wordRe.exec(code)) !== null) w[m[1].toUpperCase()] = parseFloat(m[2]);
    if (w.X === undefined && w.Y === undefined && w.Z === undefined) continue;

    for (const k of ['X', 'Y', 'Z']) if (w[k] !== undefined) cur[k] = absolute ? w[k] : cur[k] + w[k];
    for (const k of ['A', 'B', 'C']) if (w[k] !== undefined) { cur[k] = w[k]; haveOrientation = true; }

    let extrudes = false;
    if (w.E !== undefined) {
      extrudes = absE ? w.E > lastE : w.E > 0;
      if (absE) lastE = w.E;
    }
    const isTravel = g[1] === '0' || !extrudes || typeTravel;

    let axis = null;
    if (haveOrientation) {
      // Nozzle starts pointing -Z, then rotates by A (about X), B (about Y), C (about Z)
      const R = kukaABCToMatrix(cur.C || 0, cur.B || 0, cur.A || 0);
      axis = [-R[0][2], -R[1][2], -R[2][2]];
    }
    b.push([cur.X, cur.Y, cur.Z], axis, isTravel, layers.tag);
    moves++;
  }

  return b.finish({
    format: 'G-code',
    lines: lines.length,
    moves,
    unresolved: 0,
    namedPositions: 0,
    hasOrientation: haveOrientation,
    hasLayerMarkers: layers.found,
    externalAxesVarying: false,
  });
}

/**
 * Combine a multi-file KUKA upload into one program in execution order.
 * files: [{ name, text }]. Each module's .dat is placed immediately before
 * its .src (so its local point names resolve to its own values), and calls
 * to other uploaded modules are inlined where they happen. Returns
 * { text, order, mainModules, notCalled }.
 */
export function combineKrlFiles(files) {
  const modules = new Map(); // NAME -> { name, src, dat }
  for (const f of files) {
    const m = String(f.name).match(/^(.*?)\.(src|dat|krl)$/i);
    const base = (m ? m[1] : f.name).replace(/^.*[\\/]/, '');
    const key = base.toUpperCase();
    const ext = m ? m[2].toLowerCase() : 'src';
    if (!modules.has(key)) modules.set(key, { name: base, src: '', dat: '' });
    const mod = modules.get(key);
    if (ext === 'dat') mod.dat += (mod.dat ? '\n' : '') + f.text;
    else mod.src += (mod.src ? '\n' : '') + f.text;
  }

  // A call is a line holding just "NAME()" / "NAME( )" for an uploaded module
  const callOf = (line) => {
    const code = line.split(';')[0];
    const m = code.match(/^\s*([A-Za-z_][\w]*)\s*\(\s*\)\s*$/);
    return m && modules.has(m[1].toUpperCase()) ? m[1].toUpperCase() : null;
  };
  const called = new Set();
  for (const [, mod] of modules) {
    for (const line of mod.src.split(/\r?\n/)) {
      const c = callOf(line);
      if (c) called.add(c);
    }
  }

  const order = [];
  const expand = (key, stack) => {
    const mod = modules.get(key);
    if (!mod || stack.includes(key) || stack.length > 8) return '';
    order.push(mod.name);
    const out = [];
    if (mod.dat) out.push(mod.dat);
    for (const line of mod.src.split(/\r?\n/)) {
      const c = callOf(line);
      if (c && c !== key) out.push(expand(c, stack.concat(key)));
      else out.push(line);
    }
    return out.join('\n');
  };

  const keys = [...modules.keys()].sort();
  const roots = keys.filter(k => !called.has(k) && modules.get(k).src);
  const parts = roots.map(k => expand(k, []));
  // Modules never reached from a root (e.g. no main program uploaded)
  const reached = new Set(order.map(n => n.toUpperCase()));
  const notCalled = keys.filter(k => !reached.has(k));
  for (const k of notCalled) parts.push(expand(k, []));
  return {
    text: parts.join('\n'),
    order,
    mainModules: roots.map(k => modules.get(k).name),
    notCalled: notCalled.map(k => modules.get(k).name),
  };
}

export function parseToolpath(text, fileName = '') {
  const ext = fileName.split('.').pop().toLowerCase();
  if (ext === 'src' || ext === 'dat' || ext === 'krl') return parseKukaSRC(text);
  if (['gcode', 'nc', 'gco', 'g', 'ngc', 'tap'].includes(ext)) return parseGcodeToolpath(text);
  // Sniff
  if (/^\s*(DEF\s|&ACCESS|S?LIN\s*\{|S?PTP\s*\{)/im.test(text)) return parseKukaSRC(text);
  return parseGcodeToolpath(text);
}

// ─── LAYER DETECTION & NORMAL FITTING ─────────────────────────────────────────
function layerFromIndices(tp, idx) {
  const n = idx.length;
  const c = [0, 0, 0];
  const axis = [0, 0, 0];
  let axisCount = 0;
  for (const i of idx) {
    c[0] += tp.pos[i * 3]; c[1] += tp.pos[i * 3 + 1]; c[2] += tp.pos[i * 3 + 2];
    const a = [tp.toolAxis[i * 3], tp.toolAxis[i * 3 + 1], tp.toolAxis[i * 3 + 2]];
    if (a[0] || a[1] || a[2]) { axis[0] += a[0]; axis[1] += a[1]; axis[2] += a[2]; axisCount++; }
  }
  c[0] /= n; c[1] /= n; c[2] /= n;
  const cov = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (const i of idx) {
    const d = [tp.pos[i * 3] - c[0], tp.pos[i * 3 + 1] - c[1], tp.pos[i * 3 + 2] - c[2]];
    for (let r = 0; r < 3; r++) for (let s = 0; s < 3; s++) cov[r][s] += d[r] * d[s];
  }
  for (let r = 0; r < 3; r++) for (let s = 0; s < 3; s++) cov[r][s] /= n;
  const eig = eigSym3(cov);
  return {
    indices: idx,
    centroid: c,
    fitNormal: eig.vectors[0],
    // thickness (out-of-plane spread) relative to in-plane extent
    planarity: eig.values[1] > 0 ? Math.max(0, eig.values[0]) / eig.values[1] : 1,
    extent: Math.sqrt(Math.max(0, eig.values[1])),
    toolNormal: axisCount ? normalize(axis) : null,
  };
}

/**
 * Split print moves into layers.
 * opts.layerHeight  height jump (mm) that starts a new layer when no markers exist
 * opts.angleTol     tool-axis change (deg) that starts a new layer when no markers exist
 */
export function detectLayers(tp, opts = {}) {
  const layerHeight = opts.layerHeight ?? 1.0;
  const angleTol = opts.angleTol ?? 2;
  // Markers only count if at least two different layers carry print moves
  let useMarkers = tp.stats.hasLayerMarkers && opts.useMarkers !== false;
  if (useMarkers) {
    let first = null, distinct = false;
    for (let i = 0; i < tp.count && !distinct; i++) {
      if (tp.travel[i]) continue;
      if (first === null) first = tp.layerTag[i];
      else if (tp.layerTag[i] !== first) distinct = true;
    }
    useMarkers = distinct;
  }
  // A bead starts where the preceding travel move ended: that point is
  // deposited too, so it belongs to the layer with the bead.
  const beadStart = (i) => i > 0 && tp.travel[i - 1] && !tp.travel[i];
  const groups = [];

  if (useMarkers) {
    let curTag = null, cur = null;
    for (let i = 0; i < tp.count; i++) {
      if (tp.travel[i]) continue;
      const tag = tp.layerTag[i];
      if (tag !== curTag || !cur) { cur = { tag, idx: [] }; groups.push(cur); curTag = tag; }
      if (beadStart(i)) cur.idx.push(i - 1);
      cur.idx.push(i);
    }
  } else {
    // Planar-slice fallback: a new layer whenever the tool axis turns or the
    // point leaves the current slice plane by more than half a layer height.
    let cur = null, refN = null, refH = 0;
    for (let i = 0; i < tp.count; i++) {
      if (tp.travel[i]) continue;
      const p = [tp.pos[i * 3], tp.pos[i * 3 + 1], tp.pos[i * 3 + 2]];
      let n = [tp.toolAxis[i * 3], tp.toolAxis[i * 3 + 1], tp.toolAxis[i * 3 + 2]];
      if (!n[0] && !n[1] && !n[2]) n = [0, 0, -1];
      const up = scale(n, -1);
      const h = dot(p, up);
      const turned = refN && angleDeg(up, refN) > angleTol;
      const jumped = refN && Math.abs(h - refH) > layerHeight * 0.5;
      if (!cur || turned || jumped) {
        cur = { tag: groups.length, idx: [] };
        groups.push(cur);
        refN = up; refH = h;
      }
      if (beadStart(i)) cur.idx.push(i - 1);
      cur.idx.push(i);
    }
  }

  return groups.filter(g => g.idx.length >= 2).map((g, k) => ({ ...layerFromIndices(tp, g.idx), tag: g.tag, order: k }));
}

/**
 * Choose each layer's build normal and make the signs consistent with the
 * direction the part grows.
 * normalSource: 'auto' | 'fit' | 'tool'
 */
export function resolveLayerNormals(layers, opts = {}) {
  const source = opts.normalSource || 'auto';
  const maxPlanarity = opts.maxPlanarity ?? 0.05;
  const minExtent = opts.minExtent ?? 2;

  // KUKA tool Z points out of the nozzle into the part; the build normal is the
  // opposite. Decide the sign from how layer centroids progress.
  let vote = 0;
  for (let k = 1; k < layers.length; k++) {
    const a = layers[k - 1], b = layers[k];
    if (!a.toolNormal || !b.toolNormal) continue;
    const d = [b.centroid[0] - a.centroid[0], b.centroid[1] - a.centroid[1], b.centroid[2] - a.centroid[2]];
    vote += dot(d, a.toolNormal);
  }
  const toolSign = vote > 0 ? 1 : -1;

  let prev = null;
  for (const L of layers) {
    const tool = L.toolNormal ? scale(L.toolNormal, toolSign) : null;
    const fitOk = L.extent >= minExtent && L.planarity <= maxPlanarity;
    let n, src;
    if (source === 'tool' && tool) { n = tool; src = 'tool'; }
    else if (source === 'fit' || (source === 'auto' && fitOk) || !tool) {
      if (L.extent >= minExtent) { n = L.fitNormal.slice(); src = 'fit'; }
      else if (prev) { n = prev.slice(); src = 'previous'; }
      else { n = [0, 0, 1]; src = 'default'; } // degenerate first layer (purge line)
    } else { n = tool; src = 'tool'; }

    // Orient by the tool axis when it is informative, else by the previous layer
    const ref = tool && Math.abs(dot(n, tool)) > 0.26 ? tool : (prev || tool || [0, 0, 1]);
    if (dot(n, ref) < 0) n = scale(n, -1);
    L.normal = normalize(n);
    L.normalSource = src;
    L.nonPlanar = L.planarity > maxPlanarity && L.extent >= minExtent;
    prev = L.normal;
  }
  return layers;
}

// ─── ORIENTATION REGIONS ──────────────────────────────────────────────────────
export function buildOrientationRegions(tp, layers, opts = {}) {
  const angleTol = opts.regionAngleTol ?? 3;
  const regions = [];
  let cur = null;

  for (const L of layers) {
    if (cur) {
      const mean = normalize(cur.normalSum);
      if (angleDeg(mean, L.normal) <= angleTol) {
        cur.layers.push(L);
        const w = L.indices.length;
        cur.normalSum = [cur.normalSum[0] + L.normal[0] * w, cur.normalSum[1] + L.normal[1] * w, cur.normalSum[2] + L.normal[2] * w];
        continue;
      }
    }
    const w = L.indices.length;
    cur = { layers: [L], normalSum: scale(L.normal, w) };
    regions.push(cur);
  }

  // Fold very short runs (first layers, lead-ins, purge lines, final
  // retract passes) into the neighbouring region with the closer angle. Their
  // own normal is not averaged in, so an odd one cannot tilt the host region.
  // Also fold regions holding under 0.5% of the printed points (e.g. a few
  // tiny corner layers at the very start or end of a print).
  const minLayers = Math.max(1, opts.minRegionLayers ?? 3);
  const minShare = opts.minRegionShare ?? 0.005;
  const pointsOf = (r) => r.layers.reduce((n, L) => n + L.indices.length, 0);
  const totalPoints = regions.reduce((n, r) => n + pointsOf(r), 0) || 1;
  const isTiny = (r) => r.layers.length < minLayers || pointsOf(r) / totalPoints < minShare;
  for (let guard = 0; regions.length > 1 && guard < 1000; guard++) {
    let k = -1;
    for (let i = 0; i < regions.length; i++) {
      if (isTiny(regions[i]) && (k < 0 || pointsOf(regions[i]) < pointsOf(regions[k]))) k = i;
    }
    if (k < 0) break;
    const tiny = regions[k];
    const n = normalize(tiny.normalSum);
    const prev = regions[k - 1], next = regions[k + 1];
    const score = (r) => (r ? angleDeg(normalize(r.normalSum), n) : Infinity);
    const host = !next || (prev && score(prev) <= score(next)) ? prev : next;
    host.layers = host.layers.concat(tiny.layers).sort((a, b) => a.order - b.order);
    regions.splice(k, 1);
  }

  return regions.map((r, id) => {
    const n = normalize(r.normalSum);
    // Bead direction: dominant in-plane direction of the deposited segments
    const S = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    const bbMin = [Infinity, Infinity, Infinity], bbMax = [-Infinity, -Infinity, -Infinity];
    let points = 0;
    for (const L of r.layers) {
      const idx = L.indices;
      for (let k = 0; k < idx.length; k++) {
        const i = idx[k];
        for (let a = 0; a < 3; a++) {
          const v = tp.pos[i * 3 + a];
          if (v < bbMin[a]) bbMin[a] = v;
          if (v > bbMax[a]) bbMax[a] = v;
        }
        points++;
        // Deposited segment: consecutive points where the move itself prints
        if (k === 0 || idx[k - 1] !== i - 1 || tp.travel[i]) continue;
        let d = [tp.pos[i * 3] - tp.pos[(i - 1) * 3], tp.pos[i * 3 + 1] - tp.pos[(i - 1) * 3 + 1], tp.pos[i * 3 + 2] - tp.pos[(i - 1) * 3 + 2]];
        d = [d[0] - n[0] * dot(d, n), d[1] - n[1] * dot(d, n), d[2] - n[2] * dot(d, n)];
        const len = norm(d);
        if (len < 1e-9) continue;
        for (let a = 0; a < 3; a++) for (let c = 0; c < 3; c++) S[a][c] += d[a] * d[c] / len;
      }
    }
    const e = eigSym3(S);
    let t = e.vectors[2];
    if (e.values[2] <= 0) {
      const ref = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
      t = normalize(cross(cross(n, ref), n));
    }
    t = normalize([t[0] - n[0] * dot(t, n), t[1] - n[1] * dot(t, n), t[2] - n[2] * dot(t, n)]);
    if (t[0] + t[1] * 1e-3 < 0) t = scale(t, -1);
    const bdir = normalize(cross(n, t));
    const inPlaneRatio = e.values[2] > 0 ? Math.max(0, e.values[1]) / e.values[2] : 1;

    const first = r.layers[0];
    return {
      id,
      normal: n,
      beadDir: t,
      transverseDir: bdir,
      beadAlignment: 1 - inPlaneRatio, // 1 = all beads parallel, 0 = fully crossed raster
      layerStart: first.order,
      layerEnd: r.layers[r.layers.length - 1].order,
      layerCount: r.layers.length,
      layerTags: [first.tag, r.layers[r.layers.length - 1].tag],
      points,
      anchor: first.centroid.slice(),
      bboxMin: bbMin,
      bboxMax: bbMax,
      tiltDeg: angleDeg(n, [0, 0, 1]),
      azimuthDeg: ((Math.atan2(n[1], n[0]) * 180 / Math.PI) + 360) % 360,
      nonPlanarLayers: r.layers.filter(L => L.nonPlanar).length,
      layerIndices: r.layers.map(L => L.order),
    };
  });
}

/**
 * One-shot analysis: toolpath → layers → regions.
 */
export function analyseToolpath(tp, opts = {}) {
  const layers = resolveLayerNormals(detectLayers(tp, opts), opts);
  const regions = buildOrientationRegions(tp, layers, opts);
  const layerRegion = new Int32Array(layers.length);
  regions.forEach(r => r.layerIndices.forEach(li => { layerRegion[li] = r.id; }));
  const pointRegion = new Int32Array(tp.count).fill(-1);
  layers.forEach((L, li) => { for (const i of L.indices) pointRegion[i] = layerRegion[li]; });

  const bbMin = [Infinity, Infinity, Infinity], bbMax = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < tp.count; i++) {
    if (pointRegion[i] < 0) continue;
    for (let a = 0; a < 3; a++) {
      const v = tp.pos[i * 3 + a];
      if (v < bbMin[a]) bbMin[a] = v;
      if (v > bbMax[a]) bbMax[a] = v;
    }
  }
  return { layers, regions, pointRegion, bboxMin: bbMin, bboxMax: bbMax };
}

// ─── REGION STRAIN TENSORS ────────────────────────────────────────────────────
/**
 * strains: { ex, ey, ez } dimensionless thermal strain along bead (X),
 * in-layer transverse (Y) and layer normal (Z) for the temperature change.
 */
export function regionStrainTensor(region, strains, averageInPlane = false) {
  const ex = averageInPlane ? (strains.ex + strains.ey) / 2 : strains.ex;
  const ey = averageInPlane ? (strains.ex + strains.ey) / 2 : strains.ey;
  const ez = strains.ez;
  const R = [region.beadDir, region.transverseDir, region.normal]; // rows = local axes
  const E = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  const d = [ex, ey, ez];
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
    let s = 0;
    for (let k = 0; k < 3; k++) s += R[k][i] * d[k] * R[k][j];
    E[i][j] = s;
  }
  return E;
}

// ─── REGISTRATION (model frame → toolpath frame) ──────────────────────────────
// Rotations use the KUKA convention R = Rz(rz) * Ry(ry) * Rx(rx), in degrees.
export function eulerToMatrix(rx, ry, rz) {
  return kukaABCToMatrix(rz, ry, rx).flat();
}

export function matrixToEuler(R) {
  const sy = Math.max(-1, Math.min(1, -R[6]));
  const ry = Math.asin(sy);
  let rx, rz;
  if (Math.abs(sy) < 0.999999) {
    rx = Math.atan2(R[7], R[8]);
    rz = Math.atan2(R[3], R[0]);
  } else {
    rx = 0;
    rz = Math.atan2(-R[1], R[4]);
  }
  const d = (a) => {
    const v = Math.round(a * 180 / Math.PI * 1000) / 1000;
    return Object.is(v, -0) ? 0 : v;
  };
  return { rx: d(rx), ry: d(ry), rz: d(rz) };
}

export function normalizeRegistration(reg) {
  const offset = (reg?.offset || [0, 0, 0]).map(Number);
  if (Array.isArray(reg?.R) && reg.R.length === 9) return { R: reg.R.map(Number), offset };
  if (reg && (reg.rx !== undefined || reg.ry !== undefined || reg.rz !== undefined)) {
    return { R: eulerToMatrix(+reg.rx || 0, +reg.ry || 0, +reg.rz || 0), offset };
  }
  const th = (+reg?.rotZ || 0) * Math.PI / 180;
  return { R: [Math.cos(th), -Math.sin(th), 0, Math.sin(th), Math.cos(th), 0, 0, 0, 1], offset };
}

/** Model point → toolpath frame */
export function applyRegistration(reg, x, y, z) {
  const { R, offset: o } = normalizeRegistration(reg);
  return [R[0] * x + R[1] * y + R[2] * z + o[0], R[3] * x + R[4] * y + R[5] * z + o[1], R[6] * x + R[7] * y + R[8] * z + o[2]];
}

/** Toolpath point → model frame */
export function invertRegistration(reg, x, y, z) {
  const { R, offset: o } = normalizeRegistration(reg);
  const a = x - o[0], b = y - o[1], c = z - o[2];
  return [R[0] * a + R[3] * b + R[6] * c, R[1] * a + R[4] * b + R[7] * c, R[2] * a + R[5] * b + R[8] * c];
}

// ─── AUTOMATIC REGISTRATION ───────────────────────────────────────────────────
// The robot program is written in the robot BASE frame and the CAD in its own
// part frame, so they rarely share axes. autoRegister finds the rigid
// transform (rotation + offset) that puts the model around the deposited
// toolpath:
//   1. candidates: the 24 axis-aligned orientations (how a part is normally
//      placed in CAM) plus principal-axis alignments of the two shapes;
//   2. each is centred on the toolpath and scored by a trimmed two-way
//      nearest-distance between toolpath points and the model surface;
//   3. the best few are refined with trimmed ICP (Kabsch).

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Area-weighted random points on a triangle soup
function sampleSurface(positions, count, rand) {
  const nt = positions.length / 9;
  const cum = new Float64Array(nt);
  let total = 0;
  for (let t = 0; t < nt; t++) {
    const o = t * 9;
    const ux = positions[o + 3] - positions[o], uy = positions[o + 4] - positions[o + 1], uz = positions[o + 5] - positions[o + 2];
    const vx = positions[o + 6] - positions[o], vy = positions[o + 7] - positions[o + 1], vz = positions[o + 8] - positions[o + 2];
    total += 0.5 * Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx);
    cum[t] = total;
  }
  const out = new Float64Array(count * 3);
  if (total <= 0) return out.subarray(0, 0);
  for (let i = 0; i < count; i++) {
    const r = rand() * total;
    let lo = 0, hi = nt - 1;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (cum[mid] < r) lo = mid + 1; else hi = mid; }
    let a = rand(), b = rand();
    if (a + b > 1) { a = 1 - a; b = 1 - b; }
    const o = lo * 9;
    for (let k = 0; k < 3; k++) {
      out[i * 3 + k] = positions[o + k] + a * (positions[o + 3 + k] - positions[o + k]) + b * (positions[o + 6 + k] - positions[o + k]);
    }
  }
  return out;
}

// Uniform-grid nearest-neighbour index over a point set
function buildNNIndex(pts, cell) {
  const map = new Map();
  // Numeric cell key (cells within +-2048 of the origin cell along each axis)
  const key = (i, j, k) => ((i + 2048) * 4096 + (j + 2048)) * 4096 + (k + 2048);
  for (let p = 0; p < pts.length / 3; p++) {
    const k = key(Math.floor(pts[p * 3] / cell), Math.floor(pts[p * 3 + 1] / cell), Math.floor(pts[p * 3 + 2] / cell));
    let arr = map.get(k);
    if (!arr) { arr = []; map.set(k, arr); }
    arr.push(p);
  }
  // Nearest point within `rings` cells; returns [index, distance] or [-1, cap]
  function nearest(x, y, z, rings = 2) {
    const ci = Math.floor(x / cell), cj = Math.floor(y / cell), ck = Math.floor(z / cell);
    let best = -1, bd = Infinity;
    for (let i = ci - rings; i <= ci + rings; i++) {
      for (let j = cj - rings; j <= cj + rings; j++) {
        for (let k = ck - rings; k <= ck + rings; k++) {
          const arr = map.get(key(i, j, k));
          if (!arr) continue;
          for (const p of arr) {
            const dx = pts[p * 3] - x, dy = pts[p * 3 + 1] - y, dz = pts[p * 3 + 2] - z;
            const d = dx * dx + dy * dy + dz * dz;
            if (d < bd) { bd = d; best = p; }
          }
        }
      }
    }
    return best < 0 ? [-1, (rings + 1) * cell] : [best, Math.sqrt(bd)];
  }
  return { nearest };
}

/**
 * Points filling the INSIDE of a closed triangle mesh: vertical rays through
 * an XY grid, sorted crossings give inside intervals (even-odd rule).
 * Deposited beads fill the part's volume, so the toolpath must be compared
 * with the volume, not the surface (interior raster lines are far from any
 * surface).
 */
function sampleVolume(positions, count, rand, res = 72) {
  const mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < positions.length; i += 3) {
    for (let k = 0; k < 3; k++) { mn[k] = Math.min(mn[k], positions[i + k]); mx[k] = Math.max(mx[k], positions[i + k]); }
  }
  const span = Math.max(mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2]) || 1;
  const h = span / res;
  const nx = Math.max(1, Math.ceil((mx[0] - mn[0]) / h)), ny = Math.max(1, Math.ceil((mx[1] - mn[1]) / h));
  // Slight offset so rays do not run exactly through shared edges/vertices
  const ox = mn[0] + h * 0.5003, oy = mn[1] + h * 0.4997;
  const cols = new Map();
  for (let t = 0; t < positions.length; t += 9) {
    const ax = positions[t], ay = positions[t + 1], az = positions[t + 2];
    const bx = positions[t + 3], by = positions[t + 4], bz = positions[t + 5];
    const cx = positions[t + 6], cy = positions[t + 7], cz = positions[t + 8];
    const den = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
    if (Math.abs(den) < 1e-12) continue; // vertical triangle: no crossing
    const i0 = Math.max(0, Math.ceil((Math.min(ax, bx, cx) - ox) / h)), i1 = Math.min(nx - 1, Math.floor((Math.max(ax, bx, cx) - ox) / h));
    const j0 = Math.max(0, Math.ceil((Math.min(ay, by, cy) - oy) / h)), j1 = Math.min(ny - 1, Math.floor((Math.max(ay, by, cy) - oy) / h));
    for (let i = i0; i <= i1; i++) {
      const x = ox + i * h;
      for (let j = j0; j <= j1; j++) {
        const y = oy + j * h;
        const w1 = ((by - cy) * (x - cx) + (cx - bx) * (y - cy)) / den;
        const w2 = ((cy - ay) * (x - cx) + (ax - cx) * (y - cy)) / den;
        const w3 = 1 - w1 - w2;
        if (w1 < 0 || w2 < 0 || w3 < 0) continue;
        const key = i * ny + j;
        let arr = cols.get(key);
        if (!arr) { arr = []; cols.set(key, arr); }
        arr.push(w1 * az + w2 * bz + w3 * cz);
      }
    }
  }
  const cells = [];
  for (const [key, zs] of cols) {
    if (zs.length < 2) continue;
    zs.sort((a, b) => a - b);
    const i = Math.floor(key / ny), j = key % ny;
    for (let k = 0; k + 1 < zs.length; k += 2) {
      for (let z = zs[k] + h / 2; z < zs[k + 1]; z += h) cells.push(ox + i * h, oy + j * h, z);
    }
  }
  const total = cells.length / 3;
  if (!total) return new Float64Array(0);
  const out = new Float64Array(Math.min(count, total) * 3);
  for (let n = 0; n < out.length / 3; n++) {
    const c = total <= count ? n : Math.floor(rand() * total);
    out[n * 3] = cells[c * 3] + (rand() - 0.5) * h;
    out[n * 3 + 1] = cells[c * 3 + 1] + (rand() - 0.5) * h;
    out[n * 3 + 2] = cells[c * 3 + 2] + (rand() - 0.5) * h;
  }
  return out;
}

function centroidOf(pts) {
  const n = pts.length / 3, c = [0, 0, 0];
  for (let i = 0; i < n; i++) { c[0] += pts[i * 3]; c[1] += pts[i * 3 + 1]; c[2] += pts[i * 3 + 2]; }
  return c.map(v => v / Math.max(1, n));
}

function principalAxes(pts) {
  const c = centroidOf(pts), n = pts.length / 3;
  const C = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < n; i++) {
    const d = [pts[i * 3] - c[0], pts[i * 3 + 1] - c[1], pts[i * 3 + 2] - c[2]];
    for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) C[r][q] += d[r] * d[q] / n;
  }
  return eigSym3(C).vectors; // ascending variance
}

const det3 = (m) => m[0] * (m[4] * m[8] - m[5] * m[7]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);

// The 24 proper rotations that map axes onto (signed) axes
function axisRotations() {
  const out = [];
  const perms = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];
  for (const p of perms) {
    for (let sgn = 0; sgn < 8; sgn++) {
      const R = new Array(9).fill(0);
      for (let r = 0; r < 3; r++) R[r * 3 + p[r]] = (sgn >> r) & 1 ? -1 : 1;
      if (det3(R) > 0) out.push(R);
    }
  }
  return out;
}

const applyR = (R, x, y, z) => [R[0] * x + R[1] * y + R[2] * z, R[3] * x + R[4] * y + R[5] * z, R[6] * x + R[7] * y + R[8] * z];

// Rigid transform (R, t) minimising |R a_i + t - b_i| (Kabsch)
function kabsch(A, B) {
  const n = A.length;
  const ca = [0, 0, 0], cb = [0, 0, 0];
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) { ca[k] += A[i][k] / n; cb[k] += B[i][k] / n; }
  const H = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < n; i++) {
    for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) H[r][q] += (A[i][r] - ca[r]) * (B[i][q] - cb[q]);
  }
  // R = V U^T from the SVD of H, via the eigenvectors of H^T H
  const HtH = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) for (let k = 0; k < 3; k++) HtH[r][q] += H[k][r] * H[k][q];
  const { values, vectors } = eigSym3(HtH);
  const V = vectors; // columns as arrays (ascending)
  const U = V.map((v, i) => {
    const s = Math.sqrt(Math.max(values[i], 1e-30));
    const hv = [0, 1, 2].map(r => (H[r][0] * v[0] + H[r][1] * v[1] + H[r][2] * v[2]) / s);
    return normalize(hv);
  });
  // Rebuild the weakest direction to keep a right-handed basis
  U[0] = normalize(cross(U[1], U[2]));
  const Vb = [V[0], V[1], V[2]];
  const build = (Vs) => {
    const R = new Array(9).fill(0);
    for (let i = 0; i < 3; i++) for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) R[r * 3 + q] += Vs[i][r] * U[i][q];
    return R;
  };
  let R = build(Vb);
  if (det3(R) < 0) R = build([scale(V[0], -1), V[1], V[2]]);
  const rc = applyR(R, ca[0], ca[1], ca[2]);
  return { R, t: [cb[0] - rc[0], cb[1] - rc[1], cb[2] - rc[2]] };
}

/**
 * Fit the model to the toolpath. Returns { R, offset, medianMM, p90MM,
 * withinMM, fractionWithin } where p_tool = R p_model + offset.
 */
export function autoRegister(modelPositions, tp, analysis, opts = {}) {
  const rand = mulberry32(opts.seed || 12345);
  // Volume samples (falls back to the surface for open / non-solid meshes)
  let model = sampleVolume(modelPositions, opts.modelSamples || 10000, rand);
  const usedVolume = model.length >= 300;
  if (!usedVolume) model = sampleSurface(modelPositions, opts.modelSamples || 10000, rand);
  if (!model.length) throw new Error('Model has no surface area');

  // Deposited bead points, sampled evenly ALONG printed segments: program
  // points alone are just the ends of straight moves (the part's outline).
  const segs = [];
  let totalLen = 0;
  for (let i = 1; i < tp.count; i++) {
    const r = analysis.pointRegion[i];
    if (r < 0 || analysis.pointRegion[i - 1] !== r || tp.travel[i]) continue;
    const len = Math.hypot(tp.pos[i * 3] - tp.pos[(i - 1) * 3], tp.pos[i * 3 + 1] - tp.pos[(i - 1) * 3 + 1], tp.pos[i * 3 + 2] - tp.pos[(i - 1) * 3 + 2]);
    if (len <= 0) continue;
    totalLen += len;
    segs.push(i, totalLen);
  }
  const want = opts.toolSamples || 3000;
  const tool = new Float64Array(want * 3);
  let n = 0;
  if (totalLen > 0) {
    const nSeg = segs.length / 2;
    for (; n < want; n++) {
      const target = rand() * totalLen;
      let lo = 0, hi = nSeg - 1;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (segs[mid * 2 + 1] < target) lo = mid + 1; else hi = mid; }
      const i = segs[lo * 2], end = segs[lo * 2 + 1], start = lo > 0 ? segs[lo * 2 - 1] : 0;
      const f = end > start ? (target - start) / (end - start) : 0;
      for (let k = 0; k < 3; k++) tool[n * 3 + k] = tp.pos[(i - 1) * 3 + k] + f * (tp.pos[i * 3 + k] - tp.pos[(i - 1) * 3 + k]);
    }
  } else {
    for (let i = 0; i < tp.count && n < want; i++) {
      if (analysis.pointRegion[i] < 0) continue;
      for (let k = 0; k < 3; k++) tool[n * 3 + k] = tp.pos[i * 3 + k];
      n++;
    }
  }
  const toolPts = tool.subarray(0, n * 3);
  if (n < 10) throw new Error('Toolpath has too few printed points to align');

  const bb = (pts) => {
    const mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < pts.length; i += 3) for (let k = 0; k < 3; k++) { mn[k] = Math.min(mn[k], pts[i + k]); mx[k] = Math.max(mx[k], pts[i + k]); }
    return { mn, mx, diag: Math.hypot(mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2]) };
  };
  const mb = bb(model), tb = bb(toolPts);
  const cell = Math.max(mb.diag, tb.diag) / 60;
  const modelIndex = buildNNIndex(model, cell);
  const toolIndex = buildNNIndex(toolPts, cell);
  const modelSub = model.subarray(0, Math.min(model.length, 2000 * 3));

  const trimmedMean = (ds, keep = 0.8) => {
    ds.sort((a, b) => a - b);
    const m = Math.max(1, Math.floor(ds.length * keep));
    let s = 0;
    for (let i = 0; i < m; i++) s += ds[i];
    return s / m;
  };
  // Symmetric trimmed nearest-distance for p_tool = R p_model + t. Candidate
  // ranking uses a quarter of the toolpath samples; the final pick uses all.
  const toolQuick = toolPts.subarray(0, Math.min(toolPts.length, 1200 * 3));
  const score = (R, t, quick = false) => {
    const Rt = [R[0], R[3], R[6], R[1], R[4], R[7], R[2], R[5], R[8]];
    const d1 = [];
    const tps = quick ? toolQuick : toolPts;
    for (let i = 0; i < tps.length; i += 3) {
      const m = applyR(Rt, tps[i] - t[0], tps[i + 1] - t[1], tps[i + 2] - t[2]);
      d1.push(modelIndex.nearest(m[0], m[1], m[2])[1]);
    }
    const d2 = [];
    const ms = quick ? modelSub.subarray(0, Math.min(modelSub.length, 800 * 3)) : modelSub;
    for (let i = 0; i < ms.length; i += 3) {
      const p = applyR(R, ms[i], ms[i + 1], ms[i + 2]);
      d2.push(toolIndex.nearest(p[0] + t[0], p[1] + t[1], p[2] + t[2])[1]);
    }
    return trimmedMean(d1) + trimmedMean(d2);
  };
  const centreOn = (R) => {
    // Match bounding-box centres of the rotated model and the toolpath
    const r = bb(Float64Array.from({ length: model.length }, (_, i) => {
      const k = i % 3, b0 = i - k;
      return applyR(R, model[b0], model[b0 + 1], model[b0 + 2])[k];
    }));
    return [0, 1, 2].map(k => (tb.mn[k] + tb.mx[k]) / 2 - (r.mn[k] + r.mx[k]) / 2);
  };

  // Candidates
  const candidates = axisRotations();
  const am = principalAxes(model), at = principalAxes(toolPts);
  for (let s = 0; s < 4; s++) {
    const sm = [1, s & 1 ? -1 : 1, s & 2 ? -1 : 1];
    const Am = am.map((v, i) => scale(v, sm[i]));
    // R maps model axes onto toolpath axes: R = sum_i at_i am_i^T
    const R = new Array(9).fill(0);
    for (let i = 0; i < 3; i++) for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) R[r * 3 + q] += at[i][r] * Am[i][q];
    if (det3(R) < 0) for (let q = 0; q < 9; q++) R[q] = -R[q];
    if (det3(R) > 0) candidates.push(R);
  }
  if (opts.initial) candidates.push(normalizeRegistration(opts.initial).R);

  const scored = candidates.map(R => {
    const t = centreOn(R);
    return { R, t, s: score(R, t, true) };
  }).sort((a, b) => a.s - b.s);

  // Symmetric trimmed ICP on the best few: pairs toolpath->model and
  // model->toolpath, worst 30% of each dropped, solved with Kabsch.
  // Keep every pair: trimming biased the fit (tested with and without
  // machining allowance); outliers are bounded by the nearest-search radius.
  const keepFrac = opts.icpKeep || 1.0;
  const rings = opts.icpRings || 2;
  const refine = ({ R, t }, iterations = opts.iterations || 60) => {
    for (let it = 0; it < iterations; it++) {
      const Rt = [R[0], R[3], R[6], R[1], R[4], R[7], R[2], R[5], R[8]];
      const fwd = [];
      for (let i = 0; i < toolPts.length; i += 3) {
        const m = applyR(Rt, toolPts[i] - t[0], toolPts[i + 1] - t[1], toolPts[i + 2] - t[2]);
        const [j, d] = modelIndex.nearest(m[0], m[1], m[2], rings);
        if (j >= 0) fwd.push([d, [model[j * 3], model[j * 3 + 1], model[j * 3 + 2]], [toolPts[i], toolPts[i + 1], toolPts[i + 2]]]);
      }
      const back = [];
      for (let i = 0; i < modelSub.length; i += 3) {
        const p = applyR(R, modelSub[i], modelSub[i + 1], modelSub[i + 2]);
        const [j, d] = toolIndex.nearest(p[0] + t[0], p[1] + t[1], p[2] + t[2], rings);
        if (j >= 0) back.push([d, [modelSub[i], modelSub[i + 1], modelSub[i + 2]], [toolPts[j * 3], toolPts[j * 3 + 1], toolPts[j * 3 + 2]]]);
      }
      const trim = (arr) => arr.sort((x, y) => x[0] - y[0]).slice(0, Math.floor(arr.length * keepFrac));
      const keep = trim(fwd).concat(trim(back));
      if (keep.length < 10) break;
      const next = kabsch(keep.map(p => p[1]), keep.map(p => p[2]));
      const moved = Math.hypot(next.t[0] - t[0], next.t[1] - t[1], next.t[2] - t[2]);
      const turned = Math.abs(next.R[0] - R[0]) + Math.abs(next.R[4] - R[4]) + Math.abs(next.R[8] - R[8]);
      R = next.R; t = next.t;
      if (moved < cell * 1e-3 && turned < 1e-6) break;
    }
    return { R, t, s: score(R, t) };
  };
  // Short refinement of the best five, then a full one on the winner
  const short = scored.slice(0, 5).map(c => refine(c, 12)).sort((a, b) => a.s - b.s);
  let best = [refine(short[0]), short[1]].sort((a, b) => a.s - b.s)[0];

  // Shell prints (walls that trace the outline, hollow inside - typical for
  // large tools): matching against the CAD volume pulls the walls inwards,
  // towards the empty middle. Detect them (most of the CAD interior is far
  // from any bead) and refine against the CAD SURFACE instead.
  const shellGap = opts.shellGapMM || Math.max(10, Math.max(mb.diag, tb.diag) / 100);
  let shell = false;
  if (usedVolume) {
    let far = 0;
    for (let i = 0; i < modelSub.length; i += 3) {
      const p = applyR(best.R, modelSub[i], modelSub[i + 1], modelSub[i + 2]);
      if (toolIndex.nearest(p[0] + best.t[0], p[1] + best.t[1], p[2] + best.t[2])[1] > shellGap) far++;
    }
    shell = far / (modelSub.length / 3) > (opts.shellFraction || 0.3);
  }
  let surface = null, surfaceIndex = null;
  if (shell) {
    surface = sampleSurface(modelPositions, opts.surfaceSamples || 15000, rand);
    // The search must reach the far wall even while walls are off-centre, or
    // those beads drop out and reward sliding onto one side: search at least
    // three shell gaps out.
    const shellRings = Math.min(8, Math.max(2, Math.ceil((3 * shellGap) / cell)));
    surfaceIndex = buildNNIndex(surface, cell);
    let { R, t } = best;
    for (let it = 0; it < (opts.iterations || 60); it++) {
      const Rt = [R[0], R[3], R[6], R[1], R[4], R[7], R[2], R[5], R[8]];
      const fwd = [];
      for (let i = 0; i < toolPts.length; i += 3) {
        const m = applyR(Rt, toolPts[i] - t[0], toolPts[i + 1] - t[1], toolPts[i + 2] - t[2]);
        const [j, d] = surfaceIndex.nearest(m[0], m[1], m[2], shellRings);
        if (j >= 0) fwd.push([d, [surface[j * 3], surface[j * 3 + 1], surface[j * 3 + 2]], [toolPts[i], toolPts[i + 1], toolPts[i + 2]]]);
      }
      const back = [];
      const sub = Math.max(3, Math.floor(surface.length / 3 / 3000)) * 3;
      for (let i = 0; i < surface.length; i += sub) {
        const p = applyR(R, surface[i], surface[i + 1], surface[i + 2]);
        const [j, d] = toolIndex.nearest(p[0] + t[0], p[1] + t[1], p[2] + t[2], shellRings);
        if (j >= 0) back.push([d, [surface[i], surface[i + 1], surface[i + 2]], [toolPts[j * 3], toolPts[j * 3 + 1], toolPts[j * 3 + 2]]]);
      }
      // Keep every bead pair: walls sit a uniform half-bead inside the
      // surface, and dropping the worst pairs let one wall slide onto the
      // surface while the opposite wall moved away. Only CAD surface far
      // from any bead (machined pockets, faces the print does not reach) is
      // ignored, by a distance cut rather than a fixed share.
      const fwdD = fwd.map(q => q[0]).sort((x, y) => x - y);
      const typical = fwdD.length ? fwdD[Math.floor(fwdD.length / 2)] : cell;
      const cut = Math.max(3 * typical, cell);
      const keep = fwd.concat(back.filter(q => q[0] <= cut));
      if (keep.length < 10) break;
      const next = kabsch(keep.map(q => q[1]), keep.map(q => q[2]));
      const moved = Math.hypot(next.t[0] - t[0], next.t[1] - t[1], next.t[2] - t[2]);
      R = next.R; t = next.t;
      if (moved < cell * 1e-3) break;
    }
    best = { R, t, s: best.s };
  }

  // Fit report: distance from beads to the CAD (surface for shell prints)
  const refPts = shell ? surface : model;
  const refIndex = shell ? surfaceIndex : modelIndex;

  const Rt = [best.R[0], best.R[3], best.R[6], best.R[1], best.R[4], best.R[7], best.R[2], best.R[5], best.R[8]];
  const ds = [];
  for (let i = 0; i < toolPts.length; i += 3) {
    const m = applyR(Rt, toolPts[i] - best.t[0], toolPts[i + 1] - best.t[1], toolPts[i + 2] - best.t[2]);
    ds.push(refIndex.nearest(m[0], m[1], m[2], 3)[1]);
  }
  void refPts;
  ds.sort((a, b) => a - b);
  const withinMM = opts.withinMM || Math.max(5, mb.diag / 100);
  return {
    R: best.R,
    offset: best.t,
    medianMM: ds[Math.floor(ds.length / 2)],
    p90MM: ds[Math.floor(ds.length * 0.9)],
    withinMM,
    fractionWithin: ds.filter(d => d <= withinMM).length / ds.length,
    sizeRatio: mb.diag / Math.max(1e-9, tb.diag),
    method: shell ? 'shell' : usedVolume ? 'volume' : 'surface',
  };
}

// ─── CONTINUOUS DISPLACEMENT FIELD ────────────────────────────────────────────
/**
 * Build the blended multi-region field.
 *   blend          transition width between regions (mm)
 *   registration   { R [9, row-major], offset [x,y,z] } maps model → toolpath
 *                  coords (p_tool = R p_model + offset); { rotZ } also accepted
 *
 * Each region expands about the centroid of its first layer; that anchor moves
 * with the region it was printed onto, so displacement is continuous through
 * the build. Near region boundaries the affine fields are blended with a
 * Gaussian weight on the toolpath occupancy of each region.
 */
export function buildLayerField(tp, analysis, regionTensors, opts = {}) {
  // Keep the voxel grid bounded (~400 cells across the part)
  const diag = Math.hypot(...[0, 1, 2].map(a => analysis.bboxMax[a] - analysis.bboxMin[a]));
  const blend = Math.max(0.1, diag / 200, opts.blend ?? 10);
  const cell = blend / 2;
  const origin = analysis.bboxMin.map(v => v - 4 * cell);
  const dims = [0, 1, 2].map(a => Math.max(1, Math.ceil((analysis.bboxMax[a] - origin[a]) / cell) + 4));

  const voxels = new Map(); // key → Map(region → count)
  const add = (x, y, z, r) => {
    const ix = Math.floor((x - origin[0]) / cell), iy = Math.floor((y - origin[1]) / cell), iz = Math.floor((z - origin[2]) / cell);
    const key = ix + dims[0] * (iy + dims[1] * iz);
    let v = voxels.get(key);
    if (!v) { v = new Map(); voxels.set(key, v); }
    v.set(r, (v.get(r) || 0) + 1);
  };
  const { pointRegion } = analysis;
  for (let i = 0; i < tp.count; i++) {
    const r = pointRegion[i];
    if (r < 0) continue;
    const x = tp.pos[i * 3], y = tp.pos[i * 3 + 1], z = tp.pos[i * 3 + 2];
    add(x, y, z, r);
    if (i > 0 && pointRegion[i - 1] === r && !tp.travel[i]) {
      const px = tp.pos[(i - 1) * 3], py = tp.pos[(i - 1) * 3 + 1], pz = tp.pos[(i - 1) * 3 + 2];
      const len = Math.hypot(x - px, y - py, z - pz);
      const steps = Math.min(10000, Math.floor(len / (cell * 0.5)));
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        add(px + (x - px) * t, py + (y - py) * t, pz + (z - pz) * t, r);
      }
    }
  }

  const flat = [];
  for (const [key, m] of voxels) for (const [r, c] of m) flat.push(key, r, c);

  const field = {
    version: 1,
    cell,
    sigma: cell,
    origin,
    dims,
    voxels: flat,
    regions: analysis.regions.map((r, i) => ({
      E: regionTensors[i].flat(), a: r.anchor.slice(), u0: [0, 0, 0], bmin: r.bboxMin.slice(), bmax: r.bboxMax.slice(),
    })),
    registration: normalizeRegistration(opts.registration),
  };

  // Chain anchors: each region's anchor follows the region it was built on,
  // and the region takes up the small rigid rotation that best keeps its
  // first layer attached to that parent (removes most of the kink at the joint).
  const ev = createLayerFieldEvaluator(field);
  for (let k = 1; k < field.regions.length; k++) {
    const g = field.regions[k];
    const a = g.a;
    const w = ev.regionWeights(a[0], a[1], a[2], k);
    let parent = -1, best = 0;
    w.forEach((val, r) => { if (r < k && val > best) { best = val; parent = r; } });
    if (parent < 0) parent = k - 1;
    g.u0 = ev.regionDisplacement(parent, a[0], a[1], a[2]);
    g.parent = parent;
    if (opts.attachRotation !== false) {
      const firstLayer = analysis.layers[analysis.regions[k].layerStart];
      const omega = attachmentRotation(tp, firstLayer.indices, a, field.regions[parent].E, g.E);
      if (omega) {
        const [wx, wy, wz] = omega;
        // E += W, with W d = omega x d
        g.E = [
          g.E[0], g.E[1] - wz, g.E[2] + wy,
          g.E[3] + wz, g.E[4], g.E[5] - wx,
          g.E[6] - wy, g.E[7] + wx, g.E[8],
        ];
      }
    }
  }
  return field;
}

// Least-squares rotation vector w minimising |(E_parent - E_child) d - w x d|^2
// over the child's first-layer points (d measured from the child anchor).
function attachmentRotation(tp, indices, a, Ep, Ec) {
  const A = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  const bv = [0, 0, 0];
  const step = Math.max(1, Math.floor(indices.length / 5000));
  for (let m = 0; m < indices.length; m += step) {
    const i = indices[m];
    const d = [tp.pos[i * 3] - a[0], tp.pos[i * 3 + 1] - a[1], tp.pos[i * 3 + 2] - a[2]];
    const D = [0, 1, 2].map(r => (Ep[r * 3] - Ec[r * 3]) * d[0] + (Ep[r * 3 + 1] - Ec[r * 3 + 1]) * d[1] + (Ep[r * 3 + 2] - Ec[r * 3 + 2]) * d[2]);
    const dd = dot(d, d);
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) A[r][c] += (r === c ? dd : 0) - d[r] * d[c];
    // w x d = D  ->  normal equations: (|d|^2 I - d d^T) w = d x D
    const dxD = cross(d, D);
    bv[0] += dxD[0]; bv[1] += dxD[1]; bv[2] += dxD[2];
  }
  // Tikhonov-regularised solve (a straight-line first layer leaves one axis free)
  const tr = A[0][0] + A[1][1] + A[2][2];
  if (tr <= 0) return null;
  for (let r = 0; r < 3; r++) A[r][r] += tr * 1e-6;
  const det = (M) => M[0][0] * (M[1][1] * M[2][2] - M[1][2] * M[2][1]) - M[0][1] * (M[1][0] * M[2][2] - M[1][2] * M[2][0]) + M[0][2] * (M[1][0] * M[2][1] - M[1][1] * M[2][0]);
  const D0 = det(A);
  if (Math.abs(D0) < 1e-300) return null;
  return [0, 1, 2].map(col => det(A.map((row, r) => row.map((v, c) => (c === col ? bv[r] : v)))) / D0);
}

/**
 * Compiled evaluator. evaluate(x,y,z) takes MODEL coordinates and returns the
 * thermal displacement [ux,uy,uz] in model coordinates plus coverage info.
 */
export function createLayerFieldEvaluator(field) {
  const { cell, sigma, origin, dims, regions } = field;
  const lookup = new Map();
  for (let i = 0; i < field.voxels.length; i += 3) {
    const key = field.voxels[i];
    let arr = lookup.get(key);
    if (!arr) { arr = []; lookup.set(key, arr); }
    arr.push(field.voxels[i + 1], field.voxels[i + 2]);
  }
  // Model -> toolpath registration: full rotation matrix M (row-major) plus
  // offset; older fields carry only a rotation about Z.
  const reg = field.registration || {};
  let M;
  if (Array.isArray(reg.R) && reg.R.length === 9) {
    M = reg.R.slice();
  } else {
    const th = (reg.rotZ || 0) * Math.PI / 180;
    M = [Math.cos(th), -Math.sin(th), 0, Math.sin(th), Math.cos(th), 0, 0, 0, 1];
  }
  const off = reg.offset || [0, 0, 0];
  const toTool = (mx, my, mz) => [
    M[0] * mx + M[1] * my + M[2] * mz + off[0],
    M[3] * mx + M[4] * my + M[5] * mz + off[1],
    M[6] * mx + M[7] * my + M[8] * mz + off[2],
  ];
  const inv2s2 = 1 / (2 * sigma * sigma);
  const R = 2;
  const maxRing = 6;
  const nR = regions.length;
  const W = new Float64Array(nR);

  const regionDisplacement = (r, x, y, z) => {
    const g = regions[r];
    const dx = x - g.a[0], dy = y - g.a[1], dz = z - g.a[2];
    const E = g.E;
    return [
      g.u0[0] + E[0] * dx + E[1] * dy + E[2] * dz,
      g.u0[1] + E[3] * dx + E[4] * dy + E[5] * dz,
      g.u0[2] + E[6] * dx + E[7] * dy + E[8] * dz,
    ];
  };

  // Gaussian occupancy weights per region around a toolpath-frame point.
  const accumulate = (x, y, z, radius) => {
    W.fill(0);
    const fx = (x - origin[0]) / cell, fy = (y - origin[1]) / cell, fz = (z - origin[2]) / cell;
    const ix = Math.floor(fx), iy = Math.floor(fy), iz = Math.floor(fz);
    let total = 0;
    for (let k = iz - radius; k <= iz + radius; k++) {
      if (k < 0 || k >= dims[2]) continue;
      for (let j = iy - radius; j <= iy + radius; j++) {
        if (j < 0 || j >= dims[1]) continue;
        for (let i = ix - radius; i <= ix + radius; i++) {
          if (i < 0 || i >= dims[0]) continue;
          const arr = lookup.get(i + dims[0] * (j + dims[1] * k));
          if (!arr) continue;
          const ddx = (i + 0.5 - fx) * cell, ddy = (j + 0.5 - fy) * cell, ddz = (k + 0.5 - fz) * cell;
          const g = Math.exp(-(ddx * ddx + ddy * ddy + ddz * ddz) * inv2s2);
          for (let m = 0; m < arr.length; m += 2) {
            const w = arr[m + 1] * g;
            W[arr[m]] += w;
            total += w;
          }
        }
      }
    }
    return total;
  };

  const nearestRegion = (x, y, z) => {
    // Expand the search shell until any occupied voxel is found
    for (let ring = R + 1; ring <= maxRing; ring += 2) {
      if (accumulate(x, y, z, ring) > 0) {
        let best = 0, bi = -1;
        for (let r = 0; r < nR; r++) if (W[r] > best) { best = W[r]; bi = r; }
        return bi;
      }
    }
    // Far from any toolpath: closest region bounding box
    let best = Infinity, bi = 0;
    for (let r = 0; r < nR; r++) {
      const { bmin, bmax } = regions[r];
      const ddx = Math.max(bmin[0] - x, 0, x - bmax[0]);
      const ddy = Math.max(bmin[1] - y, 0, y - bmax[1]);
      const ddz = Math.max(bmin[2] - z, 0, z - bmax[2]);
      const d = ddx * ddx + ddy * ddy + ddz * ddz;
      if (d < best) { best = d; bi = r; }
    }
    return bi;
  };

  const evaluateToolFrame = (x, y, z) => {
    const total = accumulate(x, y, z, R);
    if (total > 1e-12) {
      let ux = 0, uy = 0, uz = 0;
      for (let r = 0; r < nR; r++) {
        if (W[r] <= 0) continue;
        const u = regionDisplacement(r, x, y, z);
        const w = W[r] / total;
        ux += u[0] * w; uy += u[1] * w; uz += u[2] * w;
      }
      return { u: [ux, uy, uz], covered: true };
    }
    return { u: regionDisplacement(nearestRegion(x, y, z), x, y, z), covered: false };
  };

  return {
    regionDisplacement,
    regionWeights(x, y, z, exclude = -1) {
      accumulate(x, y, z, R);
      const out = Array.from(W);
      if (exclude >= 0) out[exclude] = 0;
      if (out.every(v => v <= 0)) {
        for (let ring = R + 1; ring <= maxRing; ring += 2) {
          accumulate(x, y, z, ring);
          const o2 = Array.from(W);
          if (exclude >= 0) o2[exclude] = 0;
          if (o2.some(v => v > 0)) return o2;
        }
      }
      return out;
    },
    dominantRegion(mx, my, mz) {
      const [x, y, z] = toTool(mx, my, mz);
      if (accumulate(x, y, z, R) > 0) {
        let best = 0, bi = 0;
        for (let r = 0; r < nR; r++) if (W[r] > best) { best = W[r]; bi = r; }
        return bi;
      }
      return nearestRegion(x, y, z);
    },
    evaluate(mx, my, mz) {
      const [x, y, z] = toTool(mx, my, mz);
      const { u, covered } = evaluateToolFrame(x, y, z);
      // Displacement back into the model frame: M^T u
      return {
        u: [
          M[0] * u[0] + M[3] * u[1] + M[6] * u[2],
          M[1] * u[0] + M[4] * u[1] + M[7] * u[2],
          M[2] * u[0] + M[5] * u[1] + M[8] * u[2],
        ],
        covered,
      };
    },
  };
}

// Plain-JSON form for the server STEP job.
export function serializeLayerField(field) {
  return JSON.stringify({
    version: field.version,
    cell: field.cell,
    sigma: field.sigma,
    origin: field.origin,
    dims: field.dims,
    voxels: field.voxels,
    regions: field.regions.map(r => ({ E: r.E, a: r.a, u0: r.u0, bmin: r.bmin, bmax: r.bmax })),
    registration: field.registration,
  });
}

// ─── MESH COMPENSATION ────────────────────────────────────────────────────────
/**
 * Apply the field to a triangle soup. Compensated = p - u(p) (pre-shrunk so it
 * grows into the target at temperature). Shared vertices are evaluated once.
 */
export function compensateMeshWithField(positions, field) {
  const ev = createLayerFieldEvaluator(field);
  const nv = positions.length / 3;
  const disp = new Float32Array(positions.length);
  const region = new Int32Array(nv);
  const cache = new Map();
  let maxMag = 0, sumMag = 0, uncovered = 0;
  for (let i = 0; i < nv; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    const key = `${x},${y},${z}`;
    let hit = cache.get(key);
    if (!hit) {
      const res = ev.evaluate(x, y, z);
      hit = [-res.u[0], -res.u[1], -res.u[2], res.covered ? 1 : 0, ev.dominantRegion(x, y, z)];
      cache.set(key, hit);
    }
    disp[i * 3] = hit[0]; disp[i * 3 + 1] = hit[1]; disp[i * 3 + 2] = hit[2];
    region[i] = hit[4];
    if (!hit[3]) uncovered++;
    const mag = Math.hypot(hit[0], hit[1], hit[2]);
    if (mag > maxMag) maxMag = mag;
    sumMag += mag;
  }
  const compensated = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i++) compensated[i] = positions[i] + disp[i];
  return {
    compensated,
    displacements: disp,
    vertexRegion: region,
    maxCompMM: maxMag,
    avgCompMM: nv ? sumMag / nv : 0,
    uncoveredFraction: nv ? uncovered / nv : 0,
  };
}

// Bounding box helper for Float32Array/Float64Array positions
export function positionsBBox(positions, rotZ = 0) {
  const th = rotZ * Math.PI / 180, c = Math.cos(th), s = Math.sin(th);
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < positions.length; i += 3) {
    const x = c * positions[i] - s * positions[i + 1];
    const y = s * positions[i] + c * positions[i + 1];
    const z = positions[i + 2];
    if (x < min[0]) min[0] = x; if (x > max[0]) max[0] = x;
    if (y < min[1]) min[1] = y; if (y > max[1]) max[1] = y;
    if (z < min[2]) min[2] = z; if (z > max[2]) max[2] = z;
  }
  return { min, max };
}

/**
 * Translation that drops the (rotated) model onto the toolpath: XY centres
 * coincide and the model's lowest point sits on the first layer.
 */
export function autoRegistrationOffset(modelPositions, toolBBoxMin, toolBBoxMax, rotZ = 0) {
  const bb = positionsBBox(modelPositions, rotZ);
  return [
    (toolBBoxMin[0] + toolBBoxMax[0]) / 2 - (bb.min[0] + bb.max[0]) / 2,
    (toolBBoxMin[1] + toolBBoxMax[1]) / 2 - (bb.min[1] + bb.max[1]) / 2,
    toolBBoxMin[2] - bb.min[2],
  ];
}

// Region palette (distinct, readable on the dark viewer background)
export const REGION_COLORS = [
  '#4cc9f0', '#f72585', '#b5e48c', '#ffb703', '#a78bfa', '#fb8500',
  '#06d6a0', '#ef476f', '#90e0ef', '#ffd166', '#c77dff', '#80ed99',
];
export const regionColor = (i) => REGION_COLORS[i % REGION_COLORS.length];
